/**
 * Web adapter for react-native-tts using Web SpeechSynthesis API.
 */
class TtsWeb {
    private listeners: Record<string, Set<(e: any) => void>> = {};
    private rate: number = 0.85;

    constructor() {
        this.listeners = {
            'tts-start': new Set(),
            'tts-finish': new Set(),
            'tts-error': new Set(),
            'tts-cancel': new Set(),
        };
    }

    async getInitStatus(): Promise<boolean> {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            return true;
        }
        throw new Error('Web SpeechSynthesis API not supported in this browser');
    }

    setDefaultRate(rate: number): void {
        this.rate = Math.max(0.1, Math.min(2, rate));
    }

    speak(text: string): string {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
            console.warn('[TtsWeb] SpeechSynthesis not available');
            return 'err';
        }

        const synth = window.speechSynthesis;
        synth.cancel(); // Cancel queued utterances

        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = this.rate;

        const utterId = String(Date.now());

        utterance.onstart = () => {
            this.emit('tts-start', { utteranceId: utterId });
        };

        utterance.onend = () => {
            this.emit('tts-finish', { utteranceId: utterId });
        };

        utterance.onerror = (e) => {
            this.emit('tts-error', { utteranceId: utterId, error: e.error });
        };

        synth.speak(utterance);
        return utterId;
    }

    stop(): void {
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            window.speechSynthesis.cancel();
            this.emit('tts-cancel', {});
        }
    }

    addEventListener(event: string, handler: (e: any) => void) {
        if (!this.listeners[event]) {
            this.listeners[event] = new Set();
        }
        this.listeners[event].add(handler);

        return {
            remove: () => {
                this.listeners[event]?.delete(handler);
            },
        };
    }

    removeEventListener(event: string, handler: (e: any) => void) {
        this.listeners[event]?.delete(handler);
    }

    requestInstallEngine() {
        console.warn('[TtsWeb] requestInstallEngine is a no-op on web');
    }

    private emit(event: string, data: any) {
        this.listeners[event]?.forEach((handler) => {
            try {
                handler(data);
            } catch (err) {
                console.error(`[TtsWeb] Listener error on ${event}:`, err);
            }
        });
    }
}

export default new TtsWeb();
